import type { Input } from "../core/Input";
import { GameWindow } from "./Window";

export interface MessageOptions {
  /** Characters per second. 0 reveals instantly. */
  speed?: number;
  /** Auto-close after this many seconds instead of waiting for a keypress. */
  autoAdvance?: number;
  /** Sound to blip while typing, or null for silence. */
  onType?: () => void;
}

/**
 * The bottom text window, with the obligatory typewriter reveal: first press
 * completes the line, second press dismisses it.
 */
export class MessageBox {
  private readonly window: GameWindow;
  private readonly textEl: HTMLSpanElement;
  private readonly moreEl: HTMLSpanElement;

  private full = "";
  private revealed = 0;
  private speed = 45;
  private timer = 0;
  private autoAdvance = 0;
  private onType: (() => void) | undefined;
  private resolve: (() => void) | null = null;
  private typeAccumulator = 0;

  constructor(parent: HTMLElement, private input: Input) {
    this.window = new GameWindow(parent, "", "ds-message");
    this.textEl = document.createElement("span");
    this.moreEl = document.createElement("span");
    this.moreEl.className = "ds-more";
    this.moreEl.textContent = "\u25BC";
    this.moreEl.style.visibility = "hidden";
    this.window.el.append(this.textEl, this.moreEl);
  }

  get busy(): boolean {
    return this.resolve !== null;
  }

  get visible(): boolean {
    return this.window.visible;
  }

  /** Shows one page and resolves when the player dismisses it. */
  show(text: string, options: MessageOptions = {}): Promise<void> {
    this.full = text;
    this.revealed = options.speed === 0 ? text.length : 0;
    this.speed = options.speed ?? 45;
    this.autoAdvance = options.autoAdvance ?? 0;
    this.onType = options.onType;
    this.timer = 0;
    this.typeAccumulator = 0;
    this.textEl.textContent = this.full.slice(0, this.revealed);
    this.moreEl.style.visibility = "hidden";
    this.window.show();
    return new Promise<void>((resolve) => {
      this.resolve = resolve;
    });
  }

  /** Shows several pages in sequence. */
  async showAll(pages: readonly string[], options: MessageOptions = {}): Promise<void> {
    for (const page of pages) await this.show(page, options);
  }

  /** Displays text without blocking; caller is responsible for hiding it. */
  set(text: string): void {
    this.full = text;
    this.revealed = text.length;
    this.textEl.textContent = text;
    this.moreEl.style.visibility = "hidden";
    this.window.show();
  }

  hide(): void {
    this.window.hide();
    this.finish();
  }

  update(dt: number): void {
    if (!this.resolve) return;

    const complete = this.revealed >= this.full.length;

    if (!complete) {
      if (this.speed > 0) {
        this.typeAccumulator += dt * this.speed;
        while (this.typeAccumulator >= 1 && this.revealed < this.full.length) {
          this.typeAccumulator -= 1;
          this.revealed++;
          const ch = this.full[this.revealed - 1];
          if (this.onType && ch.trim() !== "") this.onType();
        }
      } else {
        this.revealed = this.full.length;
      }
      this.textEl.textContent = this.full.slice(0, this.revealed);

      // Skip the animation on the first press rather than eating the input.
      if (this.input.consume("confirm") || this.input.consume("cancel")) {
        this.revealed = this.full.length;
        this.textEl.textContent = this.full;
      }
      return;
    }

    this.moreEl.style.visibility = this.autoAdvance > 0 ? "hidden" : "visible";

    if (this.autoAdvance > 0) {
      this.timer += dt;
      // A timed line can still be hurried along; battle text is unreadable at
      // a fixed pace once you know what it says.
      const skipped = this.input.consume("confirm") || this.input.consume("cancel");
      if (skipped || this.timer >= this.autoAdvance) {
        this.moreEl.style.visibility = "hidden";
        this.finish();
      }
      return;
    }

    if (this.input.consume("confirm") || this.input.consume("cancel")) {
      this.moreEl.style.visibility = "hidden";
      this.finish();
    }
  }

  private finish(): void {
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.();
  }
}
